import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { formatProductAttributes } from "@osmix/catalog-core/attributes";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ExternalLink, Play } from "lucide-react";
import { storefrontProductUrl } from "@/lib/storefront";
import { useQueryClient } from "@tanstack/react-query";
import { api, type Product, type ProductStatus } from "@/api";
import { productKeys, useProduct, useRejectionReasons } from "@/api/hooks/use-products";
import { Button } from "@osmix/ui/button";
import { Badge } from "@osmix/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { Separator } from "@osmix/ui/separator";
import { Skeleton } from "@osmix/ui/skeleton";
import { ArchivedCategoryNotice } from "@/components/archived-category-notice";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { complianceBlockText, ProductComplianceCard } from "@/components/product-compliance-card";
import { Select } from "@/components/ui/select";
import { formatPrice } from "@/lib/utils";
const STATUS_VARIANT: Record<ProductStatus, "default" | "secondary" | "outline" | "destructive"> = {
  published: "default",
  moderation: "secondary",
  draft: "outline",
  rejected: "destructive",
  blocked: "destructive",
  archived: "outline"
};
export function ProductDetailPage() {
  const $ = _c(2);
  const {
    id: t0
  } = useParams();
  const id = t0 === undefined ? "" : t0;
  let t1;
  if ($[0] !== id) {
    t1 = <ProductDetailContent id={id} showBack={true} />;
    $[0] = id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function ProductDetailContent({
  id,
  showBack = false
}: {
  id: string;
  showBack?: boolean;
}) {
  const {
    t,
    i18n
  } = useTranslation();
  const queryClient = useQueryClient();
  const {
    data,
    error: loadError
  } = useProduct(id);
  const product = data ?? null;
  const [actionError, setActionError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const [pending, setPending] = useState<"approve" | "reject" | "block" | null>(null);
  // Отклонение = типовая причина из справочника (обязательна) + опциональный
  // свободный комментарий модератора.
  const [rejectionCode, setRejectionCode] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const {
    data: rejectionReasons
  } = useRejectionReasons();
  const error = actionError ?? (loadError ? loadError.message : null);
  const resetReject = () => {
    setRejectionCode("");
    setRejectionReason("");
  };
  const confirmAction = async () => {
    if (!pending) return;
    setActing(true);
    try {
      if (pending === "reject" && !rejectionCode) {
        setActionError(t("productDetail.rejectReasonRequired"));
        return;
      }
      if (pending === "block" && !rejectionReason.trim()) {
        setActionError(t("products.block.reasonRequired"));
        return;
      }
      setActionError(null);
      const updated = pending === "approve" ? await api.products.approve(id) : pending === "block" ? await api.products.block(id, rejectionReason.trim()) : await api.products.reject(id, {
        reasonCode: rejectionCode,
        reason: rejectionReason.trim() || undefined
      });
      queryClient.setQueryData(productKeys.detail(id), updated);
      void queryClient.invalidateQueries({
        queryKey: productKeys.all
      });
      setPending(null);
      resetReject();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setActing(false);
    }
  };
  const richBlocks = product?.descriptionRich?.blocks.filter(block => block.text.trim()) ?? [];
  return <div className="space-y-6">
			{/* Статус и кнопки модерации на телефоне переносятся, а не тянут
       страницу вбок (было 909 px при экране 390). */}
			<div className="flex flex-wrap items-center gap-3">
				{showBack && <Button variant="outline" size="sm" asChild>
						<Link to="/products">
							<ArrowLeft className="size-4" /> {t("productDetail.back")}
						</Link>
					</Button>}
				{product && <Badge variant={STATUS_VARIANT[product.status]}>
						{t(`products.status.${product.status}`)}
					</Badge>}
				<div className="flex-1" />
				{product && <>
						{product.status === "published" && <Button variant="ghost" size="sm" asChild>
								<a href={storefrontProductUrl(product.id)} target="_blank" rel="noreferrer">
									<ExternalLink className="size-4" /> {t("productDetail.openOnStorefront")}
								</a>
							</Button>}
						{/* Документ о соответствии (17.10.2026): без него серийную карточку не публикуем. */}
						{complianceBlockText(product.compliance, t) && product.status !== "published" && <span className="text-sm text-destructive" data-testid="product-approve-blocked">
								{complianceBlockText(product.compliance, t)}
							</span>}
						<Button onClick={() => setPending("approve")} disabled={acting || product.status === "published" || Boolean(product.compliance?.blocksPublication)} title={complianceBlockText(product.compliance, t) ?? undefined} data-testid="product-approve">
							{t(product.status === "blocked" ? "products.actions.unblock" : "products.actions.approve")}
						</Button>
						<Button variant="outline" onClick={() => setPending("reject")} disabled={acting || product.status === "rejected" || product.status === "archived" || product.status === "blocked"}>
							{t("products.actions.reject")}
						</Button>
						<Button variant="outline" className="text-destructive" onClick={() => setPending("block")} disabled={acting || product.status === "blocked" || product.status === "archived"}>
							{t("products.actions.block")}
						</Button>
					</>}
			</div>

			{error && !pending && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
					{error}
				</div>}

			{!product && !error && <div className="space-y-4">
					<Skeleton className="h-8 w-2/3" />
					<Skeleton className="h-64 w-full" />
				</div>}

			{product && <>
					<ArchivedCategoryNotice product={product} />
					<div className="grid gap-6 lg:grid-cols-[1fr_320px]">
						<div className="space-y-4">
							<header className="space-y-1">
								<h1 className="text-2xl font-semibold">{product.name}</h1>
								<p className="text-sm text-muted-foreground">
									{product.brand} · {product.article}
								</p>
							</header>

							{product.photos.length > 0 || product.videoUrl ? <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
									{product.photos.map((src, i) => <img key={`${product.id}-photo-${i}`} src={src} alt="" className="aspect-square w-full rounded-lg object-cover" />)}
									{product.videoUrl && <VideoPreview src={product.videoUrl} title={t("productDetail.video")} />}
								</div> : <div className="flex aspect-video items-center justify-center rounded-lg bg-muted text-sm text-muted-foreground">
									{t("productDetail.noPhotos")}
								</div>}

							<Card>
								<CardHeader>
									<CardTitle>{t("productDetail.description")}</CardTitle>
								</CardHeader>
								<CardContent className="space-y-2 text-sm">
									{richBlocks.length > 0 ? richBlocks.map((block_0, index) => <RichDescriptionBlock key={`${block_0.type}-${index}`} block={block_0} />) : <p className="whitespace-pre-line">{product.description || t("productDetail.noDescription")}</p>}
								</CardContent>
							</Card>

							{product.keywords.length > 0 && <Card>
									<CardHeader>
										<CardTitle>{t("productDetail.keywords")}</CardTitle>
									</CardHeader>
									<CardContent className="flex flex-wrap gap-2">
										{product.keywords.map(k => <Badge key={k} variant="outline">
												{k}
											</Badge>)}
									</CardContent>
								</Card>}

							<Card>
								<CardHeader>
									<CardTitle>{t("productDetail.attributes")}</CardTitle>
								</CardHeader>
								<CardContent className="space-y-2 text-sm">
									{/* Коды кабинета («gender: women») — словами; плюс габариты упаковки:
             модератор проверяет, что по карточке посчитается доставка. */}
									{formatProductAttributes(product.categoryAttributes, i18n.language).map(([label, value]) => <DetailRow key={label} label={label}>{value}</DetailRow>)}
									<DetailRow label={t("productDetail.packaging")}>
										{product.weightGrams && product.lengthCm && product.widthCm && product.heightCm ? t("productDetail.packagingValue", {
                  weight: product.weightGrams,
                  length: product.lengthCm,
                  width: product.widthCm,
                  height: product.heightCm
                }) : t("productDetail.packagingMissing")}
									</DetailRow>
								</CardContent>
							</Card>

							{(product.customAttributes?.length || product.customization?.enabled || product.videoUrl) && <Card>
									<CardHeader>
										<CardTitle>{t("productDetail.customAttributes")}</CardTitle>
									</CardHeader>
									<CardContent className="space-y-2 text-sm">
										{product.videoUrl && <DetailRow label={t("productDetail.video")}><a className="underline" href={product.videoUrl} target="_blank" rel="noreferrer">{product.videoUrl}</a></DetailRow>}
										{product.customAttributes?.map(attr => <DetailRow key={attr.label} label={attr.label}>{attr.value}</DetailRow>)}
										{product.customization?.enabled && <DetailRow label={t("productDetail.customization")}>{product.customization.options.map(o => o.name).join(", ") || "—"}</DetailRow>}
									</CardContent>
								</Card>}

							<Card>
								<CardHeader>
									<CardTitle>{t("productDetail.returns")}</CardTitle>
								</CardHeader>
								<CardContent className="text-sm text-muted-foreground">
									{product.returnPolicy?.customOrderException ? t("productDetail.returnsCustomException") : t("productDetail.returnsStandard")}
									{product.returnPolicy?.notes ? ` · ${product.returnPolicy.notes}` : ""}
								</CardContent>
							</Card>

							{[...(product.badges ?? []), ...(product.tags ?? [])].length > 0 && <Card>
									<CardHeader>
										<CardTitle>{t("productDetail.tagsBadges")}</CardTitle>
									</CardHeader>
									<CardContent className="flex flex-wrap gap-2">
										{[...(product.badges ?? []), ...(product.tags ?? [])].map(item => <Badge key={item} variant="outline">{item}</Badge>)}
									</CardContent>
								</Card>}
						</div>

						<aside className="space-y-4">
							<ProductComplianceCard product={product} />
							{product.moderationHistory && product.moderationHistory.length > 0 && <Card>
									<CardHeader>
										<CardTitle>{t("productDetail.moderationHistory")}</CardTitle>
									</CardHeader>
									<CardContent className="space-y-3 text-sm">
										{product.latestRejectionReason && <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-destructive">
												{t("productDetail.latestRejection", {
                  reason: product.latestRejectionReason
                })}
											</div>}
										{product.moderationHistory.map(event => <div key={event.id} className="rounded-md border p-2">
												<div className="font-medium">
													{t(`products.status.${event.oldStatus}`)} → {t(`products.status.${event.newStatus}`)}
												</div>
												<div className="text-muted-foreground">{new Date(event.createdAt).toLocaleString("ru-RU")}</div>
												{event.moderatorId && <div className="text-muted-foreground">{t("productDetail.moderator", {
                    id: event.moderatorId
                  })}</div>}
												{event.reason && <div className="mt-1">{event.reason}</div>}
											</div>)}
									</CardContent>
								</Card>}

							<ProductFacetsCard product={product} />

							<Card>
								<CardHeader>
									<CardTitle>{t("productDetail.summary")}</CardTitle>
								</CardHeader>
								<CardContent className="space-y-3 text-sm">
									<DetailRow label={t("products.col.price")}>
										<span className="tabular-nums">
											{formatPrice(product.price)}
										</span>
									</DetailRow>
									<DetailRow label={t("products.col.stock")}>
										<span className="tabular-nums">{product.stock}</span>
									</DetailRow>
									{product.minOrderQty != null && product.minOrderQty > 1 && <DetailRow label={t("productDetail.minOrderQty")}>
											<span className="tabular-nums">{product.minOrderQty}</span>
										</DetailRow>}
									<Separator />
									<DetailRow label={t("products.col.category")}>
										{product.category || "—"}
									</DetailRow>
									<DetailRow label={t("productDetail.color")}>
										{product.color || "—"}
									</DetailRow>
									<DetailRow label={t("productDetail.sizes")}>
										{product.sizes.length ? product.sizes.join(", ") : "—"}
									</DetailRow>
									<Separator />
									<DetailRow label={t("productDetail.created")}>
										{new Date(product.createdAt).toLocaleString("ru-RU")}
									</DetailRow>
									<DetailRow label={t("productDetail.updated")}>
										{new Date(product.updatedAt).toLocaleString("ru-RU")}
									</DetailRow>
									<DetailRow label="ID">
										<code className="text-xs">{product.id}</code>
									</DetailRow>
								</CardContent>
							</Card>
						</aside>
					</div>
				</>}

			<ConfirmDialog open={!!pending} busy={acting} title={pending && product ? `${t(`products_dialog.${pending}Title`)} — ${product.name}` : ""} description={pending ? t(`products_dialog.${pending}Body`) : undefined} confirmLabel={pending ? t(`products_dialog.${pending}Label`) : undefined} confirmVariant={pending === "approve" ? "default" : "destructive"} onConfirm={confirmAction} onClose={() => {
      setPending(null);
      setActionError(null);
      resetReject();
    }}>
				{actionError && pending && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive">
						{actionError}
					</div>}
				{pending === "block" && <div className="space-y-1.5">
						<label className="text-sm font-medium" htmlFor="block-reason">
							{t("products.block.reasonLabel")}
						</label>
						<textarea id="block-reason" value={rejectionReason} onChange={e_0 => setRejectionReason(e_0.target.value)} placeholder={t("products.block.reasonPlaceholder")} className="min-h-24 w-full rounded-md border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-ring" />
					</div>}
				{pending === "reject" && <div className="space-y-3">
						{/* Типовая причина из справочника — обязательна; продавец увидит
          её подпись. Комментарий уточняет, что именно не так. */}
						<div className="space-y-1.5">
							<label className="text-sm font-medium" htmlFor="reject-reason-code">
								{t("productDetail.rejectReasonLabel")}
							</label>
							<Select id="reject-reason-code" value={rejectionCode} onChange={e_1 => setRejectionCode(e_1.target.value)}>
								<option value="">{t("productDetail.rejectReasonSelect")}</option>
								{(rejectionReasons ?? []).map(r => <option key={r.code} value={r.code}>
										{r.label}
									</option>)}
							</Select>
						</div>
						<div className="space-y-1.5">
							<label className="text-sm font-medium" htmlFor="reject-reason-comment">
								{t("productDetail.rejectCommentLabel")}
							</label>
							<textarea id="reject-reason-comment" value={rejectionReason} onChange={e_2 => setRejectionReason(e_2.target.value)} placeholder={t("productDetail.rejectReasonPlaceholder")} className="min-h-24 w-full rounded-md border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-ring" />
						</div>
					</div>}
			</ConfirmDialog>
		</div>;
}
const SECTIONS = ["clothing", "shoes", "accessories", "beauty", "home", "kids", "other"] as const;
const GENDERS = ["female", "male", "unisex", "kids"] as const;

/**
 * Раздел и пол товара для фильтров витрины и подборок главной. По умолчанию
 * наследуются от категории; здесь модератор ставит поправку для конкретного
 * товара (например, унисекс-вещь в «Женской одежде») или снимает её.
 */
function ProductFacetsCard({
  product
}: {
  product: Product;
}) {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const [section, setSection] = useState(product.section ?? "");
  const [gender, setGender] = useState(product.gender ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = section !== (product.section ?? "") || gender !== (product.gender ?? "");
  const save = async (reset: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await api.products.setFacets(product.id, reset ? {
        section: null,
        gender: null
      } : {
        section: (section || null) as (typeof SECTIONS)[number] | null,
        gender: (gender || null) as (typeof GENDERS)[number] | null
      });
      const fresh = await api.products.get(product.id);
      queryClient.setQueryData(productKeys.detail(product.id), fresh);
      setSection(fresh.section ?? "");
      setGender(fresh.gender ?? "");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return <Card data-testid="product-facets">
			<CardHeader>
				<CardTitle>{t("facets.productTitle")}</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3 text-sm">
				<p className="text-xs text-muted-foreground">
					{product.facetsInherited === false ? t("facets.productOverridden") : t("facets.productInherited")}
				</p>
				<label className="flex flex-col gap-1">
					<span className="text-muted-foreground">{t("facets.section")}</span>
					<Select value={section} onChange={e_0 => setSection(e_0.target.value)} disabled={busy}>
						<option value="">{t("facets.none")}</option>
						{SECTIONS.map(s => <option key={s} value={s}>
								{t(`facets.sections.${s}`)}
							</option>)}
					</Select>
				</label>
				<label className="flex flex-col gap-1">
					<span className="text-muted-foreground">{t("facets.gender")}</span>
					<Select value={gender} onChange={e_1 => setGender(e_1.target.value)} disabled={busy}>
						<option value="">{t("facets.none")}</option>
						{GENDERS.map(g => <option key={g} value={g}>
								{t(`facets.genders.${g}`)}
							</option>)}
					</Select>
				</label>
				{error && <p className="text-destructive">{error}</p>}
				<div className="flex flex-wrap gap-2">
					<Button size="sm" disabled={busy || !dirty} onClick={() => void save(false)}>
						{t("facets.save")}
					</Button>
					{product.facetsInherited === false && <Button size="sm" variant="outline" disabled={busy} onClick={() => void save(true)}>
							{t("facets.reset")}
						</Button>}
				</div>
			</CardContent>
		</Card>;
}
function RichDescriptionBlock(t0) {
  const $ = _c(6);
  const {
    block
  } = t0;
  if (block.type === "heading") {
    let t1;
    if ($[0] !== block.text) {
      t1 = <h3 className="font-semibold text-foreground">{block.text}</h3>;
      $[0] = block.text;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (block.type === "bullet") {
    let t1;
    if ($[2] !== block.text) {
      t1 = <p className="text-muted-foreground">• {block.text}</p>;
      $[2] = block.text;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] !== block.text) {
    t1 = <p className="whitespace-pre-line text-muted-foreground">{block.text}</p>;
    $[4] = block.text;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
function videoEmbedUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "");
    if (host === "youtu.be") return `https://www.youtube.com/embed/${parsed.pathname.slice(1)}`;
    if (host === "youtube.com" || host === "m.youtube.com") {
      const id = parsed.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}`;
      if (parsed.pathname.startsWith("/shorts/")) return `https://www.youtube.com/embed/${parsed.pathname.split("/")[2]}`;
      if (parsed.pathname.startsWith("/embed/")) return url;
    }
  } catch {
    return null;
  }
  return null;
}
const isDirectVideo = (url: string): boolean => /\.(mp4|webm|ogg)(\?|#|$)/i.test(url);
function VideoPreview(t0) {
  const $ = _c(13);
  const {
    src,
    title
  } = t0;
  let t1;
  if ($[0] !== src) {
    t1 = videoEmbedUrl(src);
    $[0] = src;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const embed = t1;
  if (isDirectVideo(src)) {
    let t2;
    if ($[2] !== src) {
      t2 = <video src={src} controls={true} className="aspect-square w-full rounded-lg bg-black object-contain" />;
      $[2] = src;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (embed) {
    let t2;
    if ($[4] !== embed || $[5] !== title) {
      t2 = <iframe src={embed} title={title} className="aspect-square w-full rounded-lg border-0 bg-black" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen={true} />;
      $[4] = embed;
      $[5] = title;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Play className="size-8" fill="currentColor" />;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== title) {
    t3 = <span>{title}</span>;
    $[8] = title;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== src || $[11] !== t3) {
    t4 = <a href={src} target="_blank" rel="noreferrer" className="flex aspect-square w-full flex-col items-center justify-center gap-2 rounded-lg bg-muted text-primary underline">{t2}{t3}</a>;
    $[10] = src;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function DetailRow(t0) {
  const $ = _c(7);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-muted-foreground">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <span className="text-right">{children}</span>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex justify-between gap-4">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}